import type { EventPayload } from '@side/analytics';

export enum TrackHomePageEvent {
  CustomizeHomePageAcctSettingsLinkClick,
  CustomizeHomePageBackButtonClick,
  CustomizeHomePageButtonClick,
  CustomizeHomePageDisplayNameBlur,
  CustomizeHomePageDisplayNameChange,
  CustomizeHomePageLanding,
  CustomizeHomePagePreviewQuickLink,
  CustomizeHomePageQuickLinkDragAndDrop,
  CustomizeHomePageQuickLinksReordered,
  CustomizeHomePageSaveButtonClick,
  CustomizeHomePageUnsavedChangesCancelled,
  CustomizeHomePageUnsavedChangesConfirmed,
  CustomizeHomePageUnsavedChangesWarningPresented,
  FactsAndFiguresAgentFilterDeselectClick,
  FactsAndFiguresAgentFilterDoneClick,
  FactsAndFiguresAgentFilterSelectClick,
  FactsAndFiguresAgentSearchUpdate,
  FactsAndFiguresDateFilterTypeSelect,
  FactsAndFiguresDateFilterStartDateInput,
  FactsAndFiguresDateFilterEndDateInput,
  FactsAndFiguresDateFilterDoneClick,
  FactsAndFiguresMobileFilterDoneClick,
  FactsAndFiguresMobileFilterClearAllClick,
  FactsAndFiguresMobileSegmentTapClick,
  FactsAndFiguresMobileSegmentTooltipViewAllButtonClick,
  FactsAndFiguresMount,
  FactsAndFiguresSegmentClick,
  FactsAndFiguresSegmentHover,
  FactsAndFiguresSegmentShowMoreClick,
  FactsAndFiguresSegmentShowLessClick,
  HomePageLanding,
  NeedsAttentionCardClick,
  NeedsAttentionCardMount,
  NeedsAttentionCarouselPaginationClick,
  NeedsAttentionModalInteractionClick,
  NeedsAttentionEmptyMessageDismissed,
  NeedsAttentionEmptyMessageMount,
  NeedsAttentionModalClose,
  NeedsAttentionModalMount,
  PickUpWhereYouLeftOffClick,
  QuickLinkClick,
  SearchBarClear,
  SearchBarUpdate,
  SearchNoResultsViewAllClick,
  SearchResultGroupSelection,
  SearchResultSingleSelection,
  SideListingsButtonClick,
  SideListingsCardMount,
  SideListingsCardClick,
}

type EventTypeLabels = Record<TrackHomePageEvent, string>;

export const HomePageEventLabels: EventTypeLabels = {
  [TrackHomePageEvent.CustomizeHomePageAcctSettingsLinkClick]:
    'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageBackButtonClick]: 'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageButtonClick]: 'HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageDisplayNameBlur]: 'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageDisplayNameChange]:
    'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageLanding]: 'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePagePreviewQuickLink]: 'HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageQuickLinkDragAndDrop]: 'HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageQuickLinksReordered]: 'HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageSaveButtonClick]: 'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageUnsavedChangesCancelled]:
    'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageUnsavedChangesConfirmed]:
    'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.CustomizeHomePageUnsavedChangesWarningPresented]:
    'CUSTOMIZE_HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresAgentFilterDeselectClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresAgentFilterDoneClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresAgentFilterSelectClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresAgentSearchUpdate]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresDateFilterStartDateInput]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresDateFilterEndDateInput]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresDateFilterDoneClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresMobileFilterDoneClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresMobileFilterClearAllClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresMobileSegmentTapClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresMobileSegmentTooltipViewAllButtonClick]:
    'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresMount]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresSegmentClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresSegmentHover]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresSegmentShowMoreClick]: 'HOME_PAGE',
  [TrackHomePageEvent.FactsAndFiguresSegmentShowLessClick]: 'HOME_PAGE',
  [TrackHomePageEvent.HomePageLanding]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionCardClick]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionCardMount]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionCarouselPaginationClick]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionModalInteractionClick]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionEmptyMessageDismissed]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionEmptyMessageMount]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionModalClose]: 'HOME_PAGE',
  [TrackHomePageEvent.NeedsAttentionModalMount]: 'HOME_PAGE',
  [TrackHomePageEvent.PickUpWhereYouLeftOffClick]: 'HOME_PAGE',
  [TrackHomePageEvent.QuickLinkClick]: 'HOME_PAGE',
  [TrackHomePageEvent.SearchBarClear]: 'HOME_PAGE',
  [TrackHomePageEvent.SearchBarUpdate]: 'HOME_PAGE',
  [TrackHomePageEvent.SearchNoResultsViewAllClick]: 'HOME_PAGE',
  [TrackHomePageEvent.SearchResultGroupSelection]: 'HOME_PAGE',
  [TrackHomePageEvent.SearchResultSingleSelection]: 'HOME_PAGE',
  [TrackHomePageEvent.SideListingsButtonClick]: 'HOME_PAGE',
  [TrackHomePageEvent.SideListingsCardMount]: 'HOME_PAGE',
  [TrackHomePageEvent.SideListingsCardClick]: 'HOME_PAGE',
};

type EventTypePayloads = Record<TrackHomePageEvent, EventPayload>;

export const HomePageEventsMap: EventTypePayloads = {
  [TrackHomePageEvent.CustomizeHomePageAcctSettingsLinkClick]: {
    eventContainer: 'customize_home_page',
    eventTarget: 'account_settings_link',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.CustomizeHomePageBackButtonClick]: {
    eventContainer: 'customize_home_page_header',
    eventTarget: 'back_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.CustomizeHomePageButtonClick]: {
    eventContainer: 'home_page_header',
    eventTarget: 'customize_home_page_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.CustomizeHomePageDisplayNameBlur]: {
    eventContainer: 'customize_home_page',
    eventTarget: 'display_name',
    eventType: 'blur',
    task: 'update_input',
  },
  [TrackHomePageEvent.CustomizeHomePageDisplayNameChange]: {
    eventContainer: 'customize_home_page',
    eventTarget: 'display_name',
    eventType: 'input',
    task: 'update_input',
  },
  [TrackHomePageEvent.CustomizeHomePageLanding]: {
    eventContainer: 'customize_home_page',
    eventTarget: 'customize_home_page',
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.CustomizeHomePagePreviewQuickLink]: {
    eventContainer: '', // dynamic
    eventTarget: 'preview_link',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.CustomizeHomePageQuickLinkDragAndDrop]: {
    eventContainer: '', // dynamic
    eventTarget: 'link_card',
    eventType: 'drag_and_drop',
    task: 'reorder',
  },
  [TrackHomePageEvent.CustomizeHomePageQuickLinksReordered]: {
    eventContainer: 'shown_link_cards',
    eventTarget: 'link_card',
    eventType: 'load',
    task: 'reorder',
  },
  [TrackHomePageEvent.CustomizeHomePageSaveButtonClick]: {
    eventContainer: 'customize_home_page',
    eventTarget: 'save_changes_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackHomePageEvent.CustomizeHomePageUnsavedChangesCancelled]: {
    eventContainer: 'abandon_modal',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'exit',
  },
  [TrackHomePageEvent.CustomizeHomePageUnsavedChangesConfirmed]: {
    eventContainer: 'abandon_modal',
    eventTarget: 'continue_without_saving_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.CustomizeHomePageUnsavedChangesWarningPresented]: {
    eventContainer: 'abandon_modal',
    eventTarget: 'abandon_modal',
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.FactsAndFiguresAgentFilterDeselectClick]: {
    eventContainer: 'status_snapshot_agent_filter',
    eventTarget: 'delete_filter_button',
    eventType: 'click',
    task: 'deselect',
  },
  [TrackHomePageEvent.FactsAndFiguresAgentFilterDoneClick]: {
    eventContainer: 'status_snapshot_agent_filter',
    eventTarget: 'apply_filters_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackHomePageEvent.FactsAndFiguresAgentFilterSelectClick]: {
    eventContainer: 'status_snapshot_agent_filter',
    eventTarget: 'filter_checkbox',
    eventType: 'click',
    task: 'select',
  },
  [TrackHomePageEvent.FactsAndFiguresAgentSearchUpdate]: {
    eventContainer: 'status_snapshot_agent_filter',
    eventTarget: 'search_field',
    eventType: 'blur',
    task: 'modify_input',
  },
  [TrackHomePageEvent.FactsAndFiguresDateFilterDoneClick]: {
    eventContainer: 'status_snapshot_date_filter',
    eventTarget: 'apply_filters_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackHomePageEvent.FactsAndFiguresDateFilterTypeSelect]: {
    eventContainer: 'status_snapshot_date_filter',
    eventTarget: 'filter_checkbox',
    eventType: 'click',
    task: 'select',
  },
  [TrackHomePageEvent.FactsAndFiguresDateFilterStartDateInput]: {
    eventContainer: 'status_snapshot_date_filter',
    eventTarget: 'start',
    eventType: 'blur',
    task: 'modify_input',
  },
  [TrackHomePageEvent.FactsAndFiguresDateFilterEndDateInput]: {
    eventContainer: 'status_snapshot_date_filter',
    eventTarget: 'end',
    eventType: 'blur',
    task: 'modify_input',
  },
  [TrackHomePageEvent.FactsAndFiguresMobileFilterDoneClick]: {
    eventContainer: 'status_snapshot_mobile_filter',
    eventTarget: 'apply_filters_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackHomePageEvent.FactsAndFiguresMobileFilterClearAllClick]: {
    eventContainer: 'status_snapshot_mobile_filter',
    eventTarget: 'clear_all_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackHomePageEvent.FactsAndFiguresMobileSegmentTapClick]: {
    eventContainer: 'transaction_status_snapshot',
    eventTarget: 'status_snapshot_segment',
    eventType: 'click',
    task: 'view',
  },
  [TrackHomePageEvent.FactsAndFiguresMobileSegmentTooltipViewAllButtonClick]: {
    eventContainer: 'status_snapshot_tooltip',
    eventTarget: 'view_all_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.FactsAndFiguresMount]: {
    eventContainer: 'facts_and_figures',
    eventTarget: 'transaction_status_snapshot',
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.FactsAndFiguresSegmentClick]: {
    eventContainer: 'transaction_status_snapshot',
    eventTarget: 'status_snapshot_segment',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.FactsAndFiguresSegmentHover]: {
    eventContainer: 'transaction_status_snapshot',
    eventTarget: 'status_snapshot_segment',
    eventType: 'hover',
    task: 'view',
  },
  [TrackHomePageEvent.FactsAndFiguresSegmentShowMoreClick]: {
    eventContainer: 'status_snapshot_segment',
    eventTarget: 'show_more_button',
    eventType: 'click',
    task: 'view',
  },
  [TrackHomePageEvent.FactsAndFiguresSegmentShowLessClick]: {
    eventContainer: 'status_snapshot_segment',
    eventTarget: 'show_less_button',
    eventType: 'click',
    task: 'view',
  },
  [TrackHomePageEvent.HomePageLanding]: {
    eventContainer: 'home_page',
    eventTarget: 'home_page',
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.NeedsAttentionCardClick]: {
    eventContainer: 'attention_cards',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'view',
  },
  [TrackHomePageEvent.NeedsAttentionCardMount]: {
    eventContainer: 'attention_cards',
    eventTarget: '', // dynamic
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.NeedsAttentionCarouselPaginationClick]: {
    eventContainer: 'attention_cards',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'paginate',
  },
  [TrackHomePageEvent.NeedsAttentionModalInteractionClick]: {
    eventContainer: 'attention_modal',
    eventTarget: 'transaction_item',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.NeedsAttentionEmptyMessageDismissed]: {
    eventContainer: 'attention_banner_empty',
    eventTarget: 'close_button',
    eventType: 'click',
    task: 'view',
  },
  [TrackHomePageEvent.NeedsAttentionEmptyMessageMount]: {
    eventContainer: 'attention_cards',
    eventTarget: 'attention_banner_empty',
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.NeedsAttentionModalClose]: {
    eventContainer: 'attention_modal',
    eventTarget: 'attention_modal',
    eventType: 'click',
    task: 'exit',
  },
  [TrackHomePageEvent.NeedsAttentionModalMount]: {
    eventContainer: 'attention_modal',
    eventTarget: 'attention_modal',
    eventType: 'click',
    task: 'view',
  },
  [TrackHomePageEvent.PickUpWhereYouLeftOffClick]: {
    eventContainer: 'latest_transaction_card',
    eventTarget: 'latest_transaction_link',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.QuickLinkClick]: {
    eventContainer: 'quick_links',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.SearchBarClear]: {
    eventContainer: 'all_transaction_search',
    eventTarget: 'clear_search_button',
    eventType: 'click',
    task: 'modify_input',
  },
  [TrackHomePageEvent.SearchBarUpdate]: {
    eventContainer: 'all_transaction_search',
    eventTarget: 'search_bar',
    eventType: 'blur',
    task: '', // dynamic
  },
  [TrackHomePageEvent.SearchNoResultsViewAllClick]: {
    eventContainer: 'all_transaction_search',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.SearchResultGroupSelection]: {
    eventContainer: 'all_transaction_search',
    eventTarget: 'search_result_all',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.SearchResultSingleSelection]: {
    eventContainer: 'all_transaction_search',
    eventTarget: 'search_result_single',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.SideListingsButtonClick]: {
    eventContainer: 'side_listings',
    eventTarget: 'explore_listings_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackHomePageEvent.SideListingsCardMount]: {
    eventContainer: 'side_listings',
    eventTarget: 'explore_listings_button',
    eventType: 'load',
    task: 'view',
  },
  [TrackHomePageEvent.SideListingsCardClick]: {
    eventContainer: 'side_listings',
    eventTarget: 'side_listings',
    eventType: 'click',
    task: 'navigate',
  },
};
